import Promise from 'bluebird';
import { OneIdEnv } from 'enums';

const oneIdConfig = {
    clientId: 'ESPN-ONESITE.WEB',
    responderPage: 'https://www.espn.com/login/responder/index.html',
    cssOverride: 'https://secure.espncdn.com/combiner/c?css=disneyid/core.css'
};

export const OneIdJs = {
    [OneIdEnv.STAGING]: 'https://stg.cdn.registerdisney.go.com/v2/outer/DisneyID.js',
    [OneIdEnv.PROD]: 'https://cdn.registerdisney.go.com/v2/outer/DisneyID.js'
};

let disneyIdPromise;
let oneIdInstance;
let initialized;
let initializing;

function get() {
    if (process.browser) {
        if (window.DisneyID) {
            if (oneIdInstance && (initialized || initializing)) {
                return Promise.resolve(oneIdInstance);
            }

            initializing = true;

            if (window.did) { // the page already provides initialized oneId instance
                initialized = true;
                initializing = false;
                oneIdInstance = window.did;
                return Promise.resolve(oneIdInstance);
            }

            if (window.location.pathname === '/watch/activate') {
                oneIdConfig.optionalConfigs = {
                    reporting: {
                        source: 'ott auth',
                        context: 'ott auth'
                    }
                };
            }

            // NOTE: Supports new subscribe activation code page
            // https://espnjira.disney.com/browse/WEWEB-2044
            if (window.location.pathname === '/watch/subscribe') {
                oneIdConfig.optionalConfigs = {
                    reporting: {
                        source: 'ott auth',
                        context: 'dtc',
                        subcontext1: 'subscribe'
                    }
                };
            }

            if (!/\.espn\.com$/.test(window.location.hostname)) {
                oneIdConfig.responderPage = oneIdConfig.responderPage.replace('www.espn.com', window.location.hostname);
            }
            oneIdInstance = window.DisneyID.get(oneIdConfig);
            window.did = oneIdInstance;
            return oneIdInstance
                .init()
                .then(() => {
                    initialized = true;
                    initializing = false;
                    return oneIdInstance;
                })
                .catch((error) => {
                    console.error(error);
                });
        } else {
            // TODO: support passing `oneIdEnv`
            return loadDisneyID().then(get);
        }
    } else {
        return Promise.resolve(null);
    }
}

export function loadDisneyID(oneIdEnv = OneIdEnv.PROD) {
    return disneyIdPromise || (disneyIdPromise = new Promise((resolve, reject) => {
        if (window.DisneyID) {
            resolve(window.DisneyID);
        } else {
            const url = OneIdJs[oneIdEnv];
            const script = document.createElement('script');
            script.async = true;

            script.onload = () => {
                if (window.DisneyID) {
                    resolve(window.DisneyID);
                } else {
                    reject(new Error('script loaded but window.DisneyID not found.'));
                }
            };

            script.onerror = () => {
                reject(new Error('failed to load DisneyID'));
            };

            script.src = url;
            document.head.appendChild(script);
        }
    }));
}

// `getGuest()` rejects when user is not logged in, make sure to catch
export const getGuest = () => get().then((oneId) => {
    if (!oneId) {
        return Promise.resolve();
    }
    return oneId.getGuest()
        .catch((err) => {
            // `GUEST_READ_FAILURE` when token is not valid,
            // but most likely `logout()` won't work either when this happens
            if (err === 'GUEST_READ_FAILURE') {
                return logout()
                    .then(() => {
                        return undefined;
                    })
                    .catch(() => {
                        return undefined;
                    });
            }
            // this means user is not logged in, e.g. `Session not established`
            return undefined;
        });
});

export const getLoggedInStatus = (options) =>
    get().then((oneId) => oneId && oneId.getLoggedInStatus(options))
        .then((status) => status)
        .catch(() => false);

export const launchLogin = (options) =>
    get().then((oneId) => oneId && oneId.launchLogin(options));

export const launchReauth = (options) =>
    get().then((oneId) => oneId && oneId.launchReauth(options))
        .catch(() => launchLogin(options));

export const forceTokenRefresh = () =>
    get().then((oneId) => oneId && oneId.forceTokenRefresh())
        .catch(() => undefined);

export const logout = () =>
    get().then((oneId) => oneId && oneId.logout());

export const on = (eventName, callback) =>
    get().then((oneId) => oneId && oneId.on(eventName, callback));

export const off = (eventName, callback) =>
    get().then((oneId) => oneId && oneId.off(eventName, callback));

export const getLicensePlateDataBag = (options) =>
    get().then((oneId) => oneId && oneId.getLicensePlateDataBag(options));

export const launchLicensePlate = (options) =>
    get().then((oneId) => oneId && oneId.launchLicensePlate(options));

export const Error = {
    CANCEL_BY_USER: 'CANCEL_BY_USER',
    INVALID_PAIRING_CODE: 'INVALID_PAIRING_CODE',
    MISSING_PAIRING_CODE: 'MISSING_PAIRING_CODE',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};



// WEBPACK FOOTER //
// ./app/lib/oneId/index.js